/**
 * FieldMappingsSection — F18 T18.6.x.
 *
 * Sekcja konfiguracji mapowań pól dla pojedynczej integracji ERP. MVP: tylko encja
 * `contractor` (kontrahenci). UI-only — zapisana konfiguracja NIE jest jeszcze czytana
 * przez mapper przy sync (mapper hardkodowany). Wzorzec z SellCalla.
 *
 * Otwierana przez expand-row w `IntegrationsTab`. GET `/erp-integrations/:id/field-mappings`
 * zwraca komplet: zapisane mapowania + preset domyślnych + listę pól Symfonii + listę
 * pól lokalnych. Klient edytuje, klika „Zapisz" → bulk replace POST.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Plus, RotateCcw, Save, X } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

export type FieldMappingDirection = 'to_local' | 'to_external' | 'bidirectional';

export type FieldMappingEntityType = 'contractor' | 'employee' | 'product' | 'purchase_invoice';

const ENTITY_TYPES: readonly FieldMappingEntityType[] = [
  'contractor',
  'product',
  'purchase_invoice',
] as const;

interface MappingTab {
  key: string;
  /** Sufiks i18n: `fieldMappings.entity.<labelKey>`. */
  labelKey: string;
  /** Typ encji rozumiany przez backend. Brak = zakładka-placeholder (UI-only) LUB kontener (ma children). */
  backendType?: FieldMappingEntityType;
  /** Pod-zakładki (2. poziom) — np. „Dokumenty" zawiera typy dokumentów (Faktury zakupu…). */
  children?: MappingTab[];
}

/**
 * Zakładki mapowania pól zależą od systemu ERP. FK (Finanse i Księgowość) NIE prowadzi
 * towarów/magazynu — produkty to świat Handlu (9001). Dla FK struktura odpowiada obszarom
 * synchronizacji: Kontrahenci, Pracownicy, Dokumenty. „Dokumenty" to KONTENER na typy
 * dokumentów — w środku na razie „Faktury zakupu" (mamy je), z czasem dojdą kolejne typy.
 */
function tabsForSystem(erpSystem?: string): MappingTab[] {
  if (erpSystem === 'symfonia_fk') {
    // FK: dokumenty = TYLKO EKSPORT i mapowanie pól PER NASZ TYP — mieszka na „Dokumenty z
    // Symfonii” (rozwijane pod każdym typem), NIE tu. Dlatego brak zakładki „Dokumenty”.
    return [
      { key: 'contractor', labelKey: 'contractor', backendType: 'contractor' },
      { key: 'employee', labelKey: 'employee', backendType: 'employee' },
    ];
  }
  return ENTITY_TYPES.map((et) => ({ key: et, labelKey: et, backendType: et }));
}

/** Przycisk zakładki (1. i 2. poziom) — jeden surowy <button> współdzielony przez oba paski. */
function NavTabButton({
  active,
  label,
  testid,
  onClick,
}: {
  active: boolean;
  label: string;
  testid: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`rounded px-3 py-1 text-xs font-medium transition ${
        active
          ? 'bg-bg-primary text-text-primary shadow-sm'
          : 'text-text-muted hover:text-text-primary'
      }`}
    >
      {label}
    </button>
  );
}

interface FieldDef {
  key: string;
  label: string;
  category: string;
}

interface FieldMappingRow {
  id?: string;
  local_field: string;
  external_field: string;
  direction: FieldMappingDirection;
  is_key_field: boolean;
  transform_config?: Record<string, unknown> | null;
}

interface FieldMappingsResponse {
  mappings: FieldMappingRow[];
  defaults: FieldMappingRow[];
  erpFields: FieldDef[];
  localFields: FieldDef[];
}

interface Props {
  integrationId: string;
  /** Typ systemu ERP integracji — decyduje, które encje pokazać (FK bez „Towary"). */
  erpSystem?: string;
}

const DIRECTIONS: FieldMappingDirection[] = ['to_local', 'to_external', 'bidirectional'];

export function FieldMappingsSection({ integrationId, erpSystem }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const tabs = tabsForSystem(erpSystem);

  // T18.6.x iter. 2 — przełącznik encji. Każda encja ma własną listę mappings w bazie,
  // własny preset defaults, własną listę pól. Save per encja (bulk replace WHERE entity_type=).
  // Zakładki-placeholdery (bez backendType) nie pobierają nic — pokazują UI docelowej struktury.
  const [activeKey, setActiveKey] = useState<string>('contractor');
  const [activeChildKey, setActiveChildKey] = useState<string>('');
  const topTab = tabs.find((tb) => tb.key === activeKey) ?? tabs[0]!;
  const childTabs = topTab.children ?? null;
  const activeChild = childTabs
    ? (childTabs.find((c) => c.key === activeChildKey) ?? childTabs[0]!)
    : null;
  // Encja efektywna: pod-zakładka (jeśli kontener ma children) albo sama zakładka.
  const effectiveTab = activeChild ?? topTab;
  const backendType = effectiveTab.backendType;

  // Dokumenty = TYLKO EKSPORT (my → FK), decyzja stała: kierunek jest zawsze ten sam, więc
  // kolumnę „Kierunek" chowamy, a wszystkie wiersze jadą jako `to_external`. Kontrahenci i
  // pracownicy pozostają dwukierunkowi (kolumna zostaje). Rozszerzyć o kolejne typy dokumentów.
  const isExportOnly = backendType === 'purchase_invoice';

  const dataQ = useQuery({
    queryKey: ['erp-field-mappings', integrationId, backendType ?? activeKey],
    queryFn: async () => {
      const res = await api.get<FieldMappingsResponse>(
        `/erp-integrations/${integrationId}/field-mappings`,
        { params: { entity_type: backendType } },
      );
      return res.data;
    },
    enabled: !!backendType,
    refetchOnWindowFocus: false,
  });

  const [rows, setRows] = useState<FieldMappingRow[]>([]);
  const [dirty, setDirty] = useState(false);

  // Hydratacja: gdy serwer zwróci dane, ustawiamy lokalny stan. Gdy mappings puste,
  // pokazujemy defaults (klient widzi presety od razu jako edytowalne).
  useEffect(() => {
    if (!dataQ.data) return;
    const source = dataQ.data.mappings.length > 0 ? dataQ.data.mappings : dataQ.data.defaults;
    setRows(source.map((r) => ({ ...r, direction: isExportOnly ? 'to_external' : r.direction })));
    setDirty(false);
  }, [dataQ.data, isExportOnly]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        entity_type: backendType,
        mappings: rows.map((r) => ({
          local_field: r.local_field,
          external_field: r.external_field,
          direction: r.direction,
          is_key_field: r.is_key_field,
        })),
      };
      const res = await api.post<{ count: number }>(
        `/erp-integrations/${integrationId}/field-mappings`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.settings.integrations.fieldMappings.saved'),
      });
      setDirty(false);
      queryClient.invalidateQueries({
        queryKey: ['erp-field-mappings', integrationId, backendType ?? activeKey],
      });
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      if (code === 'DUPLICATE_PAIR') {
        toast.show({
          variant: 'error',
          title: t('tenant.settings.integrations.fieldMappings.errorDuplicate'),
        });
        return;
      }
      if (code === 'UNKNOWN_FIELDS') {
        toast.show({
          variant: 'error',
          title: t('tenant.settings.integrations.fieldMappings.errorUnknownField'),
        });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.settings.integrations.fieldMappings.errorGeneric'),
      });
    },
  });

  const validationError = useMemo(() => {
    const seen = new Set<string>();
    for (const r of rows) {
      if (!r.local_field || !r.external_field) {
        return t('tenant.settings.integrations.fieldMappings.errorEmptyRow');
      }
      const k = `${r.local_field}\x00${r.external_field}`;
      if (seen.has(k)) {
        return t('tenant.settings.integrations.fieldMappings.errorDuplicateLocal');
      }
      seen.add(k);
    }
    return null;
  }, [rows, t]);

  // Paski zakładek — wspólne dla wszystkich stanów (placeholder/loading/error/tabela),
  // żeby przełącznik encji był zawsze widoczny. 2 poziomy: encje + (dla kontenerów) typy.
  const navBars = (
    <>
      <div className="flex items-center gap-1 border-b border-border-primary bg-bg-tertiary px-4 py-1.5">
        {tabs.map((tb) => (
          <NavTabButton
            key={tb.key}
            active={activeKey === tb.key}
            label={t(`tenant.settings.integrations.fieldMappings.entity.${tb.labelKey}`)}
            testid={`field-mappings-tab-${tb.key}`}
            onClick={() => setActiveKey(tb.key)}
          />
        ))}
      </div>
      {childTabs && (
        <div className="flex items-center gap-1 border-b border-border-primary bg-bg-secondary px-4 py-1.5 pl-6">
          {childTabs.map((c) => (
            <NavTabButton
              key={c.key}
              active={effectiveTab.key === c.key}
              label={t(`tenant.settings.integrations.fieldMappings.entity.${c.labelKey}`)}
              testid={`field-mappings-subtab-${c.key}`}
              onClick={() => setActiveChildKey(c.key)}
            />
          ))}
        </div>
      )}
    </>
  );

  // Zakładka-placeholder (Pracownicy / Dokumenty w FK) — UI docelowej struktury, bez pobierania.
  if (!backendType) {
    return (
      <div data-testid={`field-mappings-section-${integrationId}`} className="bg-bg-secondary">
        {navBars}
        <div
          className="px-4 py-6 text-xs text-text-secondary"
          data-testid={`field-mappings-placeholder-${activeKey}`}
        >
          {t('tenant.settings.integrations.fieldMappings.placeholder')}
        </div>
      </div>
    );
  }

  if (dataQ.isLoading) {
    return (
      <div data-testid={`field-mappings-section-${integrationId}`} className="bg-bg-secondary">
        {navBars}
        <div
          className="flex items-center justify-center py-6"
          data-testid={`field-mappings-loading-${integrationId}`}
        >
          <Spinner />
        </div>
      </div>
    );
  }

  if (dataQ.isError || !dataQ.data) {
    return (
      <div data-testid={`field-mappings-section-${integrationId}`} className="bg-bg-secondary">
        {navBars}
        <div
          className="px-4 py-3 text-xs text-error-500"
          data-testid={`field-mappings-error-${integrationId}`}
        >
          {t('tenant.settings.integrations.fieldMappings.errorLoading')}
        </div>
      </div>
    );
  }

  const { erpFields, localFields, defaults } = dataQ.data;

  const updateRow = (idx: number, patch: Partial<FieldMappingRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const removeRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        local_field: '',
        external_field: '',
        direction: isExportOnly ? 'to_external' : 'bidirectional',
        is_key_field: false,
      },
    ]);
    setDirty(true);
  };

  const restoreDefaults = () => {
    setRows(defaults.map((d) => ({ ...d, direction: isExportOnly ? 'to_external' : d.direction })));
    setDirty(true);
  };

  return (
    <div data-testid={`field-mappings-section-${integrationId}`} className="bg-bg-secondary">
      {navBars}
      <div className="flex items-center justify-between border-b border-border-primary px-4 py-2">
        <div className="text-xs font-medium text-text-secondary">
          {t('tenant.settings.integrations.fieldMappings.title')}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={restoreDefaults}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
            data-testid={`field-mappings-restore-defaults-${integrationId}`}
          >
            <RotateCcw className="h-3 w-3" />
            {t('tenant.settings.integrations.fieldMappings.restoreDefaults')}
          </button>
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
            data-testid={`field-mappings-add-row-${integrationId}`}
          >
            <Plus className="h-3 w-3" />
            {t('tenant.settings.integrations.fieldMappings.addRow')}
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div
          className="px-4 py-6 text-center text-xs text-text-tertiary"
          data-testid={`field-mappings-empty-${integrationId}`}
        >
          {t('tenant.settings.integrations.fieldMappings.empty')}
        </div>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-bg-tertiary text-text-muted">
              <th className="px-4 py-1.5 text-left font-medium">
                {t('tenant.settings.integrations.fieldMappings.col.local')}
              </th>
              <th className="px-4 py-1.5 text-left font-medium">
                {t('tenant.settings.integrations.fieldMappings.col.external')}
              </th>
              {!isExportOnly && (
                <th className="px-4 py-1.5 text-left font-medium">
                  {t('tenant.settings.integrations.fieldMappings.col.direction')}
                </th>
              )}
              <th className="px-4 py-1.5 text-left font-medium">
                {t('tenant.settings.integrations.fieldMappings.col.keyField')}
              </th>
              <th className="px-4 py-1.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {rows.map((r, idx) => (
              <tr key={idx} data-testid={`field-mappings-row-${idx}`}>
                <td className="px-4 py-1.5">
                  <Dropdown
                    value={r.local_field}
                    onChange={(v) => updateRow(idx, { local_field: v })}
                    data-testid={`field-mappings-local-${idx}`}
                    options={[
                      {
                        value: '',
                        label: t('tenant.settings.integrations.fieldMappings.selectField'),
                      },
                      ...localFields.map((f) => ({ value: f.key, label: `${f.label} (${f.key})` })),
                    ]}
                  />
                </td>
                <td className="px-4 py-1.5">
                  <Dropdown
                    value={r.external_field}
                    onChange={(v) => updateRow(idx, { external_field: v })}
                    data-testid={`field-mappings-external-${idx}`}
                    options={[
                      {
                        value: '',
                        label: t('tenant.settings.integrations.fieldMappings.selectField'),
                      },
                      ...erpFields.map((f) => ({ value: f.key, label: `${f.label} (${f.key})` })),
                    ]}
                  />
                </td>
                {!isExportOnly && (
                  <td className="px-4 py-1.5">
                    <Dropdown
                      value={r.direction}
                      onChange={(v) => updateRow(idx, { direction: v as FieldMappingDirection })}
                      data-testid={`field-mappings-direction-${idx}`}
                      options={DIRECTIONS.map((d) => ({
                        value: d,
                        label: t(`tenant.settings.integrations.fieldMappings.direction.${d}`),
                      }))}
                    />
                  </td>
                )}
                <td className="px-4 py-1.5">
                  <Checkbox
                    checked={r.is_key_field}
                    onChange={(e) => updateRow(idx, { is_key_field: e.target.checked })}
                    data-testid={`field-mappings-key-${idx}`}
                  />
                </td>
                <td className="px-4 py-1.5 text-right">
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    aria-label={t('tenant.settings.integrations.fieldMappings.removeRow')}
                    title={t('tenant.settings.integrations.fieldMappings.removeRow')}
                    className="rounded p-1 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                    data-testid={`field-mappings-remove-${idx}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="flex items-center justify-between border-t border-border-primary px-4 py-2">
        <div className="text-xs text-text-tertiary">
          {validationError ? (
            <span
              className="text-error-500"
              data-testid={`field-mappings-validation-${integrationId}`}
            >
              {validationError}
            </span>
          ) : (
            t('tenant.settings.integrations.fieldMappings.hint')
          )}
        </div>
        <Button
          size="sm"
          onClick={() => saveMutation.mutate()}
          disabled={!dirty || saveMutation.isPending || validationError !== null}
          data-testid={`field-mappings-save-${integrationId}`}
        >
          <Save className="mr-1 h-3.5 w-3.5" />
          {t('tenant.settings.integrations.fieldMappings.save')}
        </Button>
      </div>
    </div>
  );
}
